import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const VIEWPORT_PADDING = 8;
const KEYBOARD_STEP = 16;
const DRAG_THRESHOLD = 4;

const useFloatingVideoDrag = ({ bottomInset = 0, enabled, videoUrl }) => {
    const frameRef = useRef(null);
    const dragRef = useRef(null);
    const suppressClickRef = useRef(false);
    const [position, setPosition] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    const clampPosition = useCallback(
        ({ left, top }) => {
            const { width, height } = frameRef.current.getBoundingClientRect();
            const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
            const viewportHeight = document.documentElement.clientHeight || window.innerHeight;
            return {
                left: Math.max(VIEWPORT_PADDING, Math.min(left, viewportWidth - width - VIEWPORT_PADDING)),
                top: Math.max(
                    VIEWPORT_PADDING,
                    Math.min(top, viewportHeight - height - bottomInset - VIEWPORT_PADDING)
                ),
            };
        },
        [bottomInset]
    );

    useEffect(() => {
        setPosition(null);
        dragRef.current = null;
        suppressClickRef.current = false;
        setIsDragging(false);
    }, [videoUrl]);

    useLayoutEffect(() => {
        if (!enabled) {
            dragRef.current = null;
            suppressClickRef.current = false;
            setIsDragging(false);
            return;
        }
        const frame = frameRef.current;
        const keepInViewport = () => {
            const bounds = frame.getBoundingClientRect();
            setPosition((current) => {
                const next = clampPosition(current || bounds);
                return current?.left === next.left && current?.top === next.top ? current : next;
            });
        };
        keepInViewport();
        window.addEventListener('resize', keepInViewport);
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepInViewport);
        observer?.observe(frame);
        return () => {
            window.removeEventListener('resize', keepInViewport);
            observer?.disconnect();
            dragRef.current = null;
        };
    }, [clampPosition, enabled]);

    const endDrag = (event) => {
        if (dragRef.current?.pointerId !== event.pointerId) return;
        dragRef.current = null;
        setIsDragging(false);
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    return {
        frameRef,
        isDragging,
        style: enabled && position ? { ...position, right: 'auto', bottom: 'auto' } : undefined,
        handleProps: {
            onPointerDown: (event) => {
                if (!enabled || event.button !== 0 || event.isPrimary === false || dragRef.current) return;
                const { left, top } = frameRef.current.getBoundingClientRect();
                event.currentTarget.setPointerCapture(event.pointerId);
                event.currentTarget.focus({ preventScroll: true });
                dragRef.current = {
                    pointerId: event.pointerId,
                    startX: event.clientX,
                    startY: event.clientY,
                    offsetX: event.clientX - left,
                    offsetY: event.clientY - top,
                };
                suppressClickRef.current = false;
                setIsDragging(true);
                event.preventDefault();
            },
            onPointerMove: (event) => {
                const drag = dragRef.current;
                if (!enabled || !drag || drag.pointerId !== event.pointerId) return;
                if (
                    !suppressClickRef.current &&
                    Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < DRAG_THRESHOLD
                ) {
                    return;
                }
                suppressClickRef.current = true;
                setPosition(clampPosition({ left: event.clientX - drag.offsetX, top: event.clientY - drag.offsetY }));
            },
            onClickCapture: (event) => {
                if (suppressClickRef.current && event.detail > 0) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                suppressClickRef.current = false;
            },
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onLostPointerCapture: endDrag,
            onKeyDown: (event) => {
                const delta = {
                    ArrowLeft: [-KEYBOARD_STEP, 0],
                    ArrowRight: [KEYBOARD_STEP, 0],
                    ArrowUp: [0, -KEYBOARD_STEP],
                    ArrowDown: [0, KEYBOARD_STEP],
                }[event.key];
                if (!enabled || !delta) return;
                event.preventDefault();
                const { left, top } = frameRef.current.getBoundingClientRect();
                setPosition(clampPosition({ left: left + delta[0], top: top + delta[1] }));
            },
        },
    };
};

export default useFloatingVideoDrag;
